import { Check, Copy, MessageSquarePlus } from "lucide-react";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppDialog } from "@/components/AppDialog";
import { Button } from "@/components/ui/button";
import { formatDate } from "../i18n/format.ts";
import { BUILD_ID } from "./build-refresh.ts";
import {
  type NotesInline,
  type NotesRelease,
  RELEASE_NOTES,
  type ReleaseNotes,
  safeHref,
  VERSION,
} from "./release-notes.ts";

/**
 * About ESI (openspec change `release-and-ci-deploy`): this SPA's version and build ID (the API
 * may serve another build during a canary; its health reports its own) and What's new, the
 * user-facing changes of the last releases, bundled at build time. Entries stay in their source
 * language (commit subjects are English); headings are localised.
 */
export function AboutDialog(props: {
  open: boolean;
  onClose: () => void;
  returnFocus?: () => HTMLElement | null | false;
  version?: string;
  buildId?: string;
  notes?: ReleaseNotes;
  /** Opens "Send feedback" in its place (in-app-feedback); no button when absent. */
  onSendFeedback?: () => void;
}) {
  const { t } = useTranslation();
  const version = props.version ?? VERSION;
  const buildId = props.buildId ?? BUILD_ID;
  const releases = (props.notes ?? RELEASE_NOTES).releases;
  const heading = useId();
  return (
    <AppDialog
      open={props.open}
      onClose={props.onClose}
      title={t("about.title")}
      description={t("about.lead")}
      {...(props.returnFocus && { returnFocus: props.returnFocus })}
    >
      <div className="flex min-w-0 flex-col gap-6">
        <dl className="divide-y divide-line rounded-lg border border-line bg-surface-raised text-sm">
          <div className="flex items-center justify-between gap-4 px-3 py-2.5">
            <dt className="text-fg-secondary">{t("about.version")}</dt>
            <dd className="tabular font-medium text-fg">{version}</dd>
          </div>
          <div className="flex items-center justify-between gap-4 py-1.5 pr-1.5 pl-3">
            <dt className="shrink-0 text-fg-secondary">{t("about.build")}</dt>
            <dd className="flex min-w-0 items-center gap-1">
              <code className="min-w-0 select-all truncate font-mono text-[13px] text-fg">
                {buildId}
              </code>
              <CopyButton value={buildId} />
            </dd>
          </div>
        </dl>

        <section aria-labelledby={heading} className="flex flex-col gap-3">
          <h3 id={heading} className="text-sm font-semibold text-fg">
            {t("about.whatsNew")}
          </h3>
          {releases.length === 0 ? (
            <p className="text-sm text-fg-muted">{t("about.noNotes")}</p>
          ) : (
            <ol className="flex flex-col gap-5">
              {releases.map((release) => (
                <Release key={release.version} release={release} />
              ))}
            </ol>
          )}
        </section>
        {props.onSendFeedback ? (
          <div className="flex justify-end border-t border-line pt-4">
            <Button type="button" variant="outline" onClick={props.onSendFeedback}>
              <MessageSquarePlus aria-hidden="true" />
              {t("about.sendFeedback")}
            </Button>
          </div>
        ) : null}
      </div>
    </AppDialog>
  );
}

function Release({ release }: { release: NotesRelease }) {
  const { t } = useTranslation();
  const titleOf = (group: NotesRelease["groups"][number]) =>
    group.kind === "features"
      ? t("about.features")
      : group.kind === "fixes"
        ? t("about.fixes")
        : group.title;
  return (
    <li className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between gap-3">
        <h4 className="tabular text-sm font-medium text-fg">v{release.version}</h4>
        <time dateTime={release.date} className="tabular text-xs text-fg-muted">
          {formatDate(`${release.date}T12:00:00Z`)}
        </time>
      </div>
      {release.groups.length === 0 ? (
        <p className="text-sm text-fg-muted">{t("about.noUserFacing")}</p>
      ) : (
        keyed(release.groups).map(({ key, item: group }) => {
          const title = titleOf(group);
          const more = safeHref(release.notesUrl);
          return (
            <div key={key} className="flex flex-col gap-1">
              {title ? <p className="text-xs font-medium text-fg-muted">{title}</p> : null}
              <ul className="flex list-disc flex-col gap-1 pl-4 text-sm leading-relaxed text-fg marker:text-fg-muted">
                {keyed(group.entries).map(({ key, item: entry }) => (
                  // Commit subjects start lowercase: capitalised for reading only.
                  <li key={key} className="first-letter:uppercase">
                    <Inline nodes={entry} />
                  </li>
                ))}
              </ul>
              {group.more ? (
                <p className="pl-4 text-sm text-fg-muted">
                  {more ? (
                    <a
                      href={more}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-accent-text underline underline-offset-2 hover:no-underline"
                    >
                      {t("about.more", { count: group.more })}
                    </a>
                  ) : (
                    t("about.more", { count: group.more })
                  )}
                </p>
              ) : null}
            </div>
          );
        })
      )}
    </li>
  );
}

/**
 * Position keys: the notes are static for the life of the bundle and never reordered, and two
 * entries may share a text (the same subject on two layers), so text is no key.
 */
function keyed<T>(items: readonly T[]): { key: string; item: T }[] {
  return items.map((item, i) => ({ key: String(i), item }));
}

/** Text and links only: a curated entry's markup shows as text, never as HTML. */
function Inline({ nodes }: { nodes: NotesInline[] }) {
  return keyed(nodes).map(({ key, item: node }) => {
    const href = safeHref(node.href);
    return href ? (
      <a
        key={key}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-accent-text underline underline-offset-2 hover:no-underline"
      >
        {node.text}
      </a>
    ) : (
      <span key={key}>{node.text}</span>
    );
  });
}

function CopyButton({ value }: { value: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
    } catch {
      // No clipboard (permissions, insecure context): the value stays selectable.
    }
  }
  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        aria-label={t("about.copyBuild")}
        title={copied ? t("about.copied") : t("about.copyBuild")}
        onClick={() => void copy()}
      >
        {copied ? (
          <Check aria-hidden="true" className="size-4 text-success-fg" strokeWidth={2} />
        ) : (
          <Copy aria-hidden="true" className="size-4" strokeWidth={1.75} />
        )}
      </Button>
      <span role="status" className="sr-only">
        {copied ? t("about.copied") : ""}
      </span>
    </>
  );
}
